import { isEqual } from 'lodash';
// @ts-expect-error Could not find a declaration file for module openseadragon.
import OpenSeadragon from 'openseadragon';
import { debounceTime } from 'rxjs';
import { distinctUntilChanged, take } from 'rxjs/operators';
import { HttpClient } from '@angular/common/http';
import { type AfterViewInit, ChangeDetectionStrategy, Component, DestroyRef, ElementRef, HostListener, NgZone, type OnDestroy, signal, TemplateRef, ViewChild } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { DialogService } from "primeng/dynamicdialog";
import * as FlatModels from '@medsurf/flat-models';
import * as FlatActions from '@medsurf/flat-actions';
import * as FlatFacades from '@medsurf/flat-facades';
import * as FlatServices from '@medsurf/flat-services';
import { onFontLoad } from '@medsurf/helpers';
import { ModalControlComponent } from "@medsurf/flat-components";
import { Scalebar } from './elements/scalebar';
import { Container } from './elements/container';
import { Overlay } from './elements/overlay';
import { VirtualPointer } from './elements/tools/virtualPointer';
import { Marker } from './elements/marker/marker';
import { Freeform } from './elements/freeform/freeform';
import { Keymap } from './elements/marker/keymap';
import { Text } from './elements/marker/text';
import { MarkerTool } from './tools/markerTool';
import { FixedZoomify, ZipZoomify } from '@medsurf/helpers/osd';
import { Format } from '../../../common/models/format';

@Component({
    selector: 'medsurf-image',
    changeDetection: ChangeDetectionStrategy.OnPush,
    templateUrl: './image.component.html',
    styleUrls: ['./image.component.scss'],
    standalone: false
})
export class ImageComponent implements AfterViewInit, OnDestroy {
  @ViewChild('imageElement', {static: false}) imageElementRef: ElementRef;
  @ViewChild('message_draw_tool_template') messageDrawTool: TemplateRef<any>;
  
  showSpinner = signal(false);
  typedSlideAnnotationsExpanded: { [id: string]: boolean } = {};
  typedImageAnnotationExpanded: { [id: string]: boolean } = {};
  viewer: OpenSeadragon.Viewer | null = null;
  overlay: Overlay | null = null;
  scalebar: Scalebar | null = null;
  virtualPointer: VirtualPointer | null = null;
  container: Container | null = null;
  navigatorNode: HTMLElement | null = null;
  layerIndex: number;
  imageIndex: number;
  slide: FlatModels.PageEntityModels.Slide | FlatModels.PageEntityModels.Training | null;
  image: FlatModels.ImageEntityModels.Image | null;
  media: FlatModels.MediaEntityModels.MediaImage | FlatModels.MediaEntityModels.MediaDeepzoom | null;
  annotations: FlatModels.ImageSharedModels.ElementModels[];
  minZoom: number[] = [];
  mouseOverCanvas = false;
  preloadTimers: number[] = [];
  virtualPointerSelected = false;
  annotationSelected = false;
  selftestMarker: any;
  hasMoved = false;

  constructor(public alertControlFacade: FlatFacades.AlertControlFacade,
              public slideAuthorFacade: FlatFacades.SlideAuthorFacade,
              public bulkEditAuthorFacade: FlatFacades.BulkEditAuthorFacade,
              public imageSharedFacade: FlatFacades.ImageSharedFacade,
              public menuSharedFacade: FlatFacades.MenuSharedFacade,
              public mediaControlService: FlatServices.MediaControlService,
              public zone: NgZone,
              public translate: TranslateService,
              public http: HttpClient,
              public router: Router,
              public dialogService: DialogService,
              private destroyRef: DestroyRef) {
  }

  ngAfterViewInit() {
    this.slideAuthorFacade.typedSlideAnnotationsExpanded$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((typedSlideAnnotationsExpanded) => {
      this.typedSlideAnnotationsExpanded = typedSlideAnnotationsExpanded;
    });
    this.slideAuthorFacade.typedImageAnnotationExpanded$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((typedImageAnnotationExpanded) => {
      this.typedImageAnnotationExpanded = typedImageAnnotationExpanded;
    });
    this.slideAuthorFacade.layerIndex$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((layerIndex) => {
      this.layerIndex = layerIndex;
    });
    this.slideAuthorFacade.imageIndex$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((imageIndex) => {
      this.imageIndex = imageIndex;
      this.#updateLayerPreload(this.imageIndex, false);
    });
    this.slideAuthorFacade.currentSelectedSlideAnnotation$.pipe(
      takeUntilDestroyed(this.destroyRef),
      distinctUntilChanged((a, b) => isEqual(a, b)),
      debounceTime(30),
    ).subscribe((annotation) => {
      // TODO dirty fix for selection -> image can have the wrong sizes saved (layer/image change), so the selection is too small; so we wait 200 ms to let the sizes be deployed
      setTimeout(() => {
        this.annotationSelected = !!annotation;
        this.onSelectedAnnotationChange(annotation);
      });
    });
    this.slideAuthorFacade.editPanelDisplay$.pipe(
      takeUntilDestroyed(this.destroyRef),
      distinctUntilChanged((a, b) => isEqual(a, b)),
      debounceTime(20)
    ).subscribe((editPanelDisplay) => {
      if (editPanelDisplay !== FlatModels.SlideAuthorModels.EditPanelDisplay.enum.annotation && this.container) {
        this.container.deselectAll();
      }
    });
    this.bulkEditAuthorFacade.selectAnnotationSet$.pipe(
      takeUntilDestroyed(this.destroyRef),
      distinctUntilChanged((a, b) => isEqual(a, b)),
      debounceTime(20)
    ).subscribe((selectAnnotationSet) => {
      if (this.container) {
        this.container.deselectAll();
        this.annotationSelected = selectAnnotationSet.size > 0;
        for (const id of Array.from(selectAnnotationSet)) {
          const element = this.container.getElementById(id);
          if (element) {
            this.container.select(element, true);
          }
        }
        if (selectAnnotationSet.size === 0 && this.slideAuthorFacade.snapshot_editPanelDisplay() === FlatModels.SlideAuthorModels.EditPanelDisplay.enum.annotation) {
          const annotation = this.slideAuthorFacade.snapshot_currentSelectedSlideAnnotation();
          if (annotation?.id) {
            const element = this.container.getElementById(annotation.id);
            if (element) {
              this.container.select(element, true);
            }
          }
        }
      }
    });
    this.imageSharedFacade.config$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((config) => {
      this.initOpenSeadragonViewer(config);
    });
    this.imageSharedFacade.slide$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((slide) => {
      this.slide = slide;
    });
    this.imageSharedFacade.image$.pipe(
      takeUntilDestroyed(this.destroyRef),
      distinctUntilChanged((a, b) => isEqual(a, b))
    ).subscribe((image) => {
      this.image = image;
      if (this.container) {
        this.container.destroyElements();
      }
      this.initOpenSeadragonImage(this.image, this.media);
      this.initOpenSeadragonAnnotations(this.overlay, this.container, this.media, this.annotations);
    });
    this.imageSharedFacade.medias$.pipe(
      takeUntilDestroyed(this.destroyRef),
      distinctUntilChanged((a, b) => isEqual(a, b))
    ).subscribe((medias) => {
      if (this.container) {
        this.container.destroyElements();
      }
      if (medias) {
        this.initOpenSeadragonImages(medias, this.imageIndex);
      }
      // Ensure show current images
      setTimeout(() => {
        this.initOpenSeadragonImage(this.image, this.media);
        this.initOpenSeadragonAnnotations(this.overlay, this.container, this.media, this.annotations);
      });
    })
    this.imageSharedFacade.media$.pipe(
      takeUntilDestroyed(this.destroyRef),
      distinctUntilChanged((a, b) => isEqual(a, b)),
      debounceTime(10),
    ).subscribe((media) => {
      if (this.container) {
        this.container.destroyElements();
      }

      if (media?.type !== 'MediaDeepzoom' && media?.type !== 'MediaImage') return;
      this.media = media;
      this.initOpenSeadragonImage(this.image, this.media);
      this.initOpenSeadragonAnnotations(this.overlay, this.container, this.media, this.annotations);
    });
    this.imageSharedFacade.annotations$.pipe(
      takeUntilDestroyed(this.destroyRef),
      distinctUntilChanged((a, b) => isEqual(a, b)),
      debounceTime(10),
    ).subscribe((annotations) => {
      this.annotations = annotations;
      if (this.container) {
        this.container.destroyElements();
      }
      this.initOpenSeadragonAnnotations(this.overlay, this.container, this.media, this.annotations);
    });
    this.slideAuthorFacade.ofActionDispatchedAddMarkerWithPosition.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((data: { action: FlatActions.SlideAuthorActions.AddMarkerWithPosition }) => {
      const currentPositionAndZoom = this.getCurrentPositionAndZoom();
      if (!currentPositionAndZoom) {
        return;
      }
      this.slideAuthorFacade.requestAddMarker(data.action.page, data.action.image, data.action.poi, data.action.tipType, data.action.text, currentPositionAndZoom, data.action.name).subscribe({
        error: (result) => this.alertControlFacade.requestAddError(result),
      });
    });
    this.slideAuthorFacade.ofActionDispatchedUpdateMarkerAddTargetWithPosition.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((data: { action: FlatActions.SlideAuthorActions.UpdateMarkerAddTargetWithPosition }) => {
      const currentPositionAndZoom = this.getCurrentPositionAndZoom();
      if (!currentPositionAndZoom) {
        return;
      }
      this.slideAuthorFacade.requestUpdateMarkerAddTarget(data.action.marker, currentPositionAndZoom).subscribe({
        error: (result) => this.alertControlFacade.requestAddError(result),
      });
    });
    this.slideAuthorFacade.ofActionDispatchedAddFreeFormWithPosition.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((data: { action: FlatActions.SlideAuthorActions.AddFreeFormWithPosition }) => {
      const currentPositionAndZoom = this.getCurrentPositionAndZoom();
      if (!currentPositionAndZoom) {
        return;
      }
      this.slideAuthorFacade.requestAddFreeForm(data.action.page, data.action.image, data.action.poi, data.action.shape, data.action.text, currentPositionAndZoom, data.action.name).subscribe({
        error: (result) => this.alertControlFacade.requestAddError(result),
      });
    });
    this.slideAuthorFacade.ofActionDispatchedAddKeymapWithPosition.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((data: { action: FlatActions.SlideAuthorActions.AddKeymapWithPosition }) => {
      const currentPositionAndZoom = this.getCurrentPositionAndZoom();
      if (!currentPositionAndZoom) {
        return;
      }
      this.slideAuthorFacade.requestAddKeymap(data.action.page, data.action.image, data.action.poi, data.action.text, currentPositionAndZoom, data.action.name).subscribe({
        error: (result) => this.alertControlFacade.requestAddError(result),
      });
    });
    this.slideAuthorFacade.ofActionDispatchedAddTextWithPosition.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((data: { action: FlatActions.SlideAuthorActions.AddTextWithPosition }) => {
      const currentPositionAndZoom = this.getCurrentPositionAndZoom();
      if (!currentPositionAndZoom) {
        return;
      }
      this.slideAuthorFacade.requestAddText(data.action.page, data.action.image, data.action.poi, data.action.text, currentPositionAndZoom, data.action.name).subscribe({
        error: (result) => this.alertControlFacade.requestAddError(result),
      });
    });
    this.slideAuthorFacade.ofActionDispatchedUpdateAnnotationPosition.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((data: { action: FlatActions.SlideAuthorActions.UpdateAnnotationPosition }) => {
      const currentPositionAndZoom = this.getCurrentPositionAndZoom();
      if (!currentPositionAndZoom) {
        return;
      }
      
      // Get Annotation
      const annotation = structuredClone(data.action.annotation);
      
      switch (annotation.type) {
        case FlatModels.AnnotationEntityModels.AnnotationType.enum.Marker:
          this.slideAuthorFacade.requestUpdateMarkerPosition(annotation, currentPositionAndZoom).subscribe({
            error: (result) => this.alertControlFacade.requestAddError(result),
          });
          break;
        case FlatModels.AnnotationEntityModels.AnnotationType.enum.FreeForm:
          this.slideAuthorFacade.requestUpdateFreeFormPosition(annotation, currentPositionAndZoom).subscribe({
            error: (result) => this.alertControlFacade.requestAddError(result),
          });
          break;
        case FlatModels.AnnotationEntityModels.AnnotationType.enum.Keymap:
          this.slideAuthorFacade.requestUpdateKeymapPosition(annotation, currentPositionAndZoom).subscribe({
            error: (result) => this.alertControlFacade.requestAddError(result),
          });
          break;
        case FlatModels.AnnotationEntityModels.AnnotationType.enum.Text:
          this.slideAuthorFacade.requestUpdateTextPosition(annotation, currentPositionAndZoom).subscribe({
            error: (result) => this.alertControlFacade.requestAddError(result),
          });
          break;
      }
    });
    this.slideAuthorFacade.ofActionDispatchedCopyAnnotationWithPosition.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((data: { action: FlatActions.SlideAuthorActions.CopyAnnotationWithPosition }) => {
      const currentPositionAndZoom = this.getCurrentPositionAndZoom();
      
      // Get Data
      const page = structuredClone(data.action.page);
      const image = structuredClone(data.action.image);
      
      // Request Paste Annotation
      this.bulkEditAuthorFacade.requestPasteAnnotations(page, image, currentPositionAndZoom).subscribe({
        error: (result) => this.alertControlFacade.requestAddError(result),
        next: () => {
          this.typedImageAnnotationExpanded[image.id] = true;
          this.slideAuthorFacade.requestSetTypedImageAnnotationExpanded(this.typedImageAnnotationExpanded).subscribe({
            error: (result) => this.alertControlFacade.requestAddError(result),
          });
        }
      });
    });
    this.slideAuthorFacade.ofActionDispatchedActivateDrawTool.pipe(takeUntilDestroyed(this.destroyRef), debounceTime(10)).subscribe((data: { action: FlatActions.SlideAuthorActions.ActivateDrawTool }) => {
      this.handleActivateFreeTool(data.action.freeForm);
    });
    this.slideAuthorFacade.ofActionDispatchedGoHome.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.viewer.viewport.goHome(false);
    });
    this.slideAuthorFacade.ofActionDispatchedGoToAnnotation.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((data: { action: FlatActions.SlideAuthorActions.GoToAnnotation }) => {
      this.goToAnnotation(data.action.annotation);
    });
    this.slideAuthorFacade.ofActionDispatchedGoToPoi.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((data: { action: FlatActions.SlideAuthorActions.GoToPoi }) => {
      this.goToPoi(data.action.poi);
    });
    this.slideAuthorFacade.ofActionDispatchedAddPoiWithPosition.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((data: { action: FlatActions.SlideAuthorActions.AddPoiWithPosition }) => {
      const currentPositionAndZoom = this.getCurrentPositionAndZoom();
      if (!currentPositionAndZoom) {
        return;
      }
      this.slideAuthorFacade.requestAddPoi(data.action.page, data.action.image, data.action.text, currentPositionAndZoom, data.action.name).subscribe({
        error: (result) => this.alertControlFacade.requestAddError(result),
      });
    });
    this.slideAuthorFacade.ofActionDispatchedUpdatePoiPosition.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((data: { action: FlatActions.SlideAuthorActions.UpdatePoiPosition }) => {
      const currentPositionAndZoom = this.getCurrentPositionAndZoom();
      if (!currentPositionAndZoom) {
        return;
      }
      
      // Get Poi
      const poi = structuredClone(data.action.poi);
      
      // Update Poi Bounds
      poi.bounds = {
        x: currentPositionAndZoom.currentBounds.x,
        y: currentPositionAndZoom.currentBounds.y,
        width: currentPositionAndZoom.currentBounds.width,
        height: currentPositionAndZoom.currentBounds.height
      };
      
      this.slideAuthorFacade.requestUpdatePoi(poi, 'history.poi_update_position').subscribe(() => ({
        // @ts-expect-error Parameter result implicitly has an any type.
        error: (result) => this.alertControlFacade.requestAddError(result),
      }))
    });

    this.menuSharedFacade.showVirtualPointer$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(this.toggleVirtualPointer.bind(this));
    this.menuSharedFacade.showMarkers$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(this.#toggleMarkers.bind(this));

    window.addEventListener('wheel', this.handleWheel.bind(this), {passive: true});
  }

  #updateLayerPreload(imageIndex: number,
                      preload: boolean) {
    if (this.preloadTimers.length > 0) {
      this.preloadTimers.forEach(timer => clearTimeout(timer));
    }
    if (imageIndex === undefined || !this.viewer) {
      return;
    }
    const length: number = this.viewer.world.getItemCount();
    for (let i = 0; i < length; i++) {
      const item = this.viewer.world.getItemAt(i);
      if (i === imageIndex) {
        item.setOpacity(1);
        item.immediateRender = false;
      } else if (preload && (i === imageIndex + 1 || i === imageIndex - 1)) {
        this.preloadTimers.push(setTimeout(() => {
          item.setOpacity(0.001); // setting opacity to not zero enables preloading of the viewport
          item.immediateRender = true;
        }, 100));
      } else {
        if (item.getOpacity() !== 0) {
          item.setOpacity(0);
          item.immediateRender = true;
        }
      }
    }
  }

  async initOpenSeadragonViewer(config: Partial<OpenSeadragon.Options>) {
    if (config !== null && this.imageElementRef && this.imageElementRef.nativeElement) {
      // Init Viewer
      await this.zone.runOutsideAngular(async () => {
        // Destroy Viewer
        if (this.viewer !== null) {
          this.viewer.destroy();
        }
        // Create Viewer
        this.viewer = new OpenSeadragon.Viewer(Object.assign(config, {element: this.imageElementRef.nativeElement}));

        // Setup Viewer Handlers
        this.setupViewerHandlers();

        // Setup Overlay
        this.overlay = new Overlay(this.viewer);

        // Setup Scalebar
        this.scalebar = new Scalebar({
          viewer: this.viewer,
          backgroundColor: 'white',
          xOffset: 10,
          yOffset: 10,
          barThickness: 4,
        });

        // Setup Container
        this.container = new Container(this.overlay.localPaper, new Format());

        // Setup Navigator
        this.navigatorNode = this.viewer?.navigator?.element?.parentElement;

        // Show navigator
        this.#setNavigatorNode();

        // Setup Tools
        await this.setupTool(this.overlay, this.container);
      });
    }
    return true;
  }

  setupViewerHandlers() {
    this.viewer.removeAllHandlers();

    this.viewer.addHandler('open', () => {
      this.showSpinner.set(false);
      this.viewer.viewport.goHome(true);
    });

    this.viewer.addHandler('animation-start', () => {
      this.#updateLayerPreload(this.imageIndex, false);
    })

    this.viewer.addHandler('animation-finish', () => {
      this.#updateLayerPreload(this.imageIndex, true);
    })

    this.viewer.addHandler('canvas-enter', () => {
      this.mouseOverCanvas = true;
      this.viewer.canvas.focus();
    });

    this.viewer.addHandler('canvas-exit', () => {
      this.mouseOverCanvas = false;
      this.viewer.canvas.blur();
    });

    this.viewer.addHandler('animation', () => {
      if (this.virtualPointer) {
        this.virtualPointer.arrange();
      }
    });
    
    this.viewer.addHandler('canvas-key', (e: { preventDefaultAction: boolean; preventVerticalPan: boolean; preventHorizontalPan: boolean; }) => {
      if (this.annotationSelected) {
        e.preventDefaultAction = true;
        e.preventVerticalPan = true;
        e.preventHorizontalPan = true;
      }
    });

    onFontLoad('Figtree', { weight: '600'}, () => {
      this.container?.arrange();
      this.container?.update();
    })
  }

  initOpenSeadragonImages(medias: FlatModels.MediaEntityModels.MediaEntityType[],
                          imageIndex: number) {
    if (this.viewer !== null && Array.isArray(medias)) {
      this.showSpinner.set(true);
      
      // Cancel
      this.viewer?._cancelPendingImages();
      
      // Setup Tile Sources
      const tileSources: any = [];
      for (const [index, media] of medias.entries()) {
        
        // Get isActive
        const isActive = index === imageIndex;
        
        // Get isPreload
        const isPreload = index === (imageIndex - 1) || index === (imageIndex + 1);
        
        // Tile Sources
        let tileSource;
        let loadTilesWithAjax = false;
        if (media.type === FlatModels.MediaEntityModels.MediaType.enum.MediaDeepzoom) {
          const tilesUrl = this.mediaControlService.getUrl(media, 'tileFolder');
          // Get Dimensions
          const dimensions = media.dimensions;
          if (tilesUrl.endsWith(".zip")) {
            loadTilesWithAjax = true;
            tileSource = new ZipZoomify({
              tilesUrl,
              width: (dimensions?.width || 0),
              height: (dimensions?.height || 0)
            });
          } else {
            tileSource = new FixedZoomify({
              tilesUrl: tilesUrl || '/assets/images/no-image.png',
              width: (dimensions?.width || 0),
              height: (dimensions?.height || 0),
            });
          }
        } else if (media.type === FlatModels.MediaEntityModels.MediaType.enum.MediaImage) {
          tileSource = {
            type: 'image',
            url: this.mediaControlService.getUrl(media) || '/assets/images/no-image.png'
          };
        } else {
          tileSource = {
            type: 'image',
            url: '/assets/images/no-image.png'
          };
        }
        tileSources.push({
          x: 0,
          y: 0,
          loadTilesWithAjax,
          opacity: isActive ? 1 : (isPreload ? 0.001 : 0),
          tileSource
        });
        this.minZoom[index] = this.viewer.viewport.getZoom(true);
      }
      this.viewer.open(tileSources);
    }
  }

  initOpenSeadragonImage(image: FlatModels.ImageEntityModels.Image | null,
                         media: FlatModels.MediaEntityModels.MediaImage | FlatModels.MediaEntityModels.MediaDeepzoom | null) {
    if (this.viewer !== null && image && media) {
      // Navigator Background Color
      const navigatorBackgroundColor = image && image.background && image.background.color || '';

      // Setup Background Color
      this.viewer.element.style.backgroundColor = navigatorBackgroundColor.startsWith('#') ? navigatorBackgroundColor : '#ffffff';

      // Get Dimensions
      const dimensions = media && media.dimensions;

      // Update Scalebar
      this.scalebar?.updateOptions({
        pixelsPerMeter: (dimensions?.pixelPerMillimeter ? (dimensions.pixelPerMillimeter * 1000) : null)
      });
      this.scalebar?.refresh();

      // Update Container
      this.updateContainer(media);

      this.#toggleMarkers(this.menuSharedFacade.snapshot_showMarkers());
    }
  }

  updateContainer(media: FlatModels.MediaEntityModels.MediaImage | FlatModels.MediaEntityModels.MediaDeepzoom) {
    if (media.dimensions?.width && media.dimensions?.height) {
      this.container?.setCanvasSize(media.dimensions.width, media.dimensions.height);
    }
    this.container?.setMinZoom(1);
  }

  zoomIn(): void {
    const zoomInStep = 1.2;
    const maxZoom: number = this.viewer.viewport.getMaxZoom();
    const currentZoom: number = this.viewer.viewport.getZoom(true);
    if (maxZoom > currentZoom) {
      this.viewer.viewport.zoomBy(zoomInStep, null, true);
    }
  }

  toggleNavigator() {
    this.menuSharedFacade.requestToggleNavigator().subscribe({
      error: (result) => this.alertControlFacade.requestAddError(result),
    });
    this.#setNavigatorNode();
  }

  zoomOut() {
    const zoomOutStep = 0.8;
    const currentZoom: number = this.viewer.viewport.getZoom(true);
    if (this.minZoom[this.imageIndex] < currentZoom) {
      this.viewer.viewport.zoomBy(zoomOutStep, null, true);
    }
  }

  #setNavigatorNode() {
    if (this.navigatorNode) {
      this.navigatorNode.style.display = this.menuSharedFacade.snapshot_showNavigator() ? 'inline-block' : 'none';
    }
  }

  initOpenSeadragonAnnotations(overlay: Overlay | null,
                               container: Container | null,
                               media: FlatModels.MediaEntityModels.MediaImage | FlatModels.MediaEntityModels.MediaDeepzoom | null,
                               annotations: FlatModels.ImageSharedModels.ElementModels[] | null) {
    if (!overlay || !container || !media || !annotations) {
      return;
    }
    const {localPaper, mainLayer} = overlay;
    const scale = this.mediaControlService.isDeepzoom(media) ? 1 : 600 / (media.dimensions?.width || 1);
    mainLayer.visible = false;
    // const offset = (isVM) ? undefined : this.slide.images[this.sequenceId].offset;
    annotations.forEach((marker: FlatModels.ImageSharedModels.ElementModels) => {
      const markerFormat = new Format(this.slide?.defaults || {labelColor: null, pointerColor: null, labelFontSize: null});
      let element;
      switch (marker.annotation.type) {
        case FlatModels.AnnotationEntityModels.AnnotationType.enum.Text:
          element = new Text(marker, markerFormat, localPaper, mainLayer, container, scale, {x: 0, y: 0});
          break;
        case FlatModels.AnnotationEntityModels.AnnotationType.enum.Keymap:
          element = new Keymap(marker as FlatModels.ImageSharedModels.KeymapModel, markerFormat, localPaper, mainLayer, container, scale, {x: 0, y: 0});
          break;
        case FlatModels.AnnotationEntityModels.AnnotationType.enum.FreeForm:
          markerFormat.setMarkerFormat(marker);
          element = new Freeform(marker as FlatModels.ImageSharedModels.FreeformModel, markerFormat, localPaper, mainLayer, container, scale, {x: 0, y: 0});
          break;
        default:
          markerFormat.setMarkerFormat(marker);
          element = new Marker(marker as FlatModels.ImageSharedModels.MarkerModel, markerFormat, localPaper, mainLayer, container, scale, {x: 0, y: 0});
          break;
      }
      if (element) {
        container.addElement(element);
      }
    });

    // Setup Virtual Pointer
    this.setupVirtualPointer(overlay, container);

    // Resize Overlay
    overlay.resize();
    overlay.resizeCanvas();

    // Show Overlay
    this.viewer.addOnceHandler('update-viewport', () => {
      mainLayer.visible = true;
      localPaper.view.update();
    });
    this.viewer.forceRedraw();

    this.#toggleMarkers(this.menuSharedFacade.snapshot_showMarkers());
    this.slideAuthorFacade.currentSelectedSlideAnnotation$.pipe(take(1)).subscribe((annotation) => {
      this.onSelectedAnnotationChange(annotation);
    });
  }

  setupVirtualPointer(overlay: Overlay,
                      container: Container) {
    const {localPaper, mainLayer}: Overlay = overlay;

    if (this.virtualPointer) {
      this.virtualPointer.cleanUp();
      this.virtualPointer = null;
    }

    this.virtualPointer = new VirtualPointer(this.http, localPaper, mainLayer, container);
    this.virtualPointer.id = 'virtualPointer';
    this.virtualPointer.on('created', () => {
      if (!this.virtualPointer) return;
      this.virtualPointer.state = this.menuSharedFacade.snapshot_showVirtualPointer() ? 'visible' : 'hidden';
      container.addElement(this.virtualPointer);
      overlay.resize();
      overlay.resizeCanvas();
    });

    this.virtualPointer.loadFile();
  }

  async setupTool(overlay: Overlay,
                  container: Container) {
    const {localPaper} = overlay;
    const markerTool = new MarkerTool(localPaper, container, window, this.slide);
    const trackerConfig = {
      element: this.viewer.canvas,
      pressHandler: (event: MouseEvent) => this.#clickHandler(this.viewer, event, markerTool),
      dragHandler: (event: MouseEvent) => this.#dragHandler(this.viewer, event, markerTool),
      releaseHandler: (event: MouseEvent) => this.#releaseHandler(event, markerTool),
    };
    const mouseTracker: OpenSeadragon.MouseTracker = new OpenSeadragon.MouseTracker(trackerConfig);
    mouseTracker.setTracking(true);

    markerTool.removeAllListeners();

    markerTool.on('selectMarker', (event) => this.#handleSelectContainerElement(event));
    markerTool.on('selectKeymap', (event) => this.#handleSelectContainerElement(event));
    markerTool.on('selectFreeform', (event) => this.#handleSelectContainerElement(event));

    markerTool.on('moveItem', (event) => this.#handleMove(this.viewer, event));
    markerTool.on('endMoveItem', () => this.#handleEndMove(this.viewer));

    markerTool.on('unselectMarker', () => {
      this.#handleUnSelectMarker();
      if (this.overlay?.canvas.parentElement) {
        this.overlay.canvas.parentElement.style.cursor = "default";
      }
    });
    markerTool.on('updateAnnotation', () => {
      container.arrange();
      container.update();
    });
    markerTool.on('updateMoveMultipleAnnotations', (data) => {
      this.zone.run(() => {
        this.slideAuthorFacade.requestUpdateMoveMultipleAnnotations(data.annotations, data.name).subscribe({
          error: (result) => this.alertControlFacade.requestAddError(result),
        });
      });
    });
    markerTool.on('updateMarker', (data) => {
      this.zone.run(() => {
        this.slideAuthorFacade.requestUpdateMarker(data.marker, data.source, data.targets, data.name).subscribe({
          error: (result) => this.alertControlFacade.requestAddError(result),
        });
      });
    });
    markerTool.on('updateMarkerFork', (data) => {
      this.zone.run(() => {
        this.slideAuthorFacade.requestUpdateAnnotation(data.marker, data.name).subscribe({
          error: (result) => this.alertControlFacade.requestAddError(result),
        });
      });
    });
    markerTool.on('updateMarkerSource', (data) => {
      this.zone.run(() => {
        this.slideAuthorFacade.requestUpdateAnnotationSource(data.source, data.name).subscribe({
          error: (result) => this.alertControlFacade.requestAddError(result),
        });
      });
    });
    markerTool.on('updateMarkerTarget', (data) => {
      this.zone.run(() => {
        this.slideAuthorFacade.requestUpdateAnnotationTarget(data.target, data.name).subscribe({
          error: (result) => this.alertControlFacade.requestAddError(result),
        });
      });
    });
    markerTool.on('updateKeymapSource', (data) => {
      this.zone.run(() => {
        this.slideAuthorFacade.requestUpdateAnnotationSource(data.source, data.name).subscribe({
          error: (result) => this.alertControlFacade.requestAddError(result),
        });
      });
    });
    markerTool.on('updateFreeformPath', (data) => {
      this.zone.run(() => {
        this.slideAuthorFacade.requestUpdateAnnotation(data.freeform, data.name).subscribe({
          error: (result) => this.alertControlFacade.requestAddError(result),
        });
      });
    });
    markerTool.on('updateFreeformSource', (data) => {
      this.zone.run(() => {
        this.slideAuthorFacade.requestUpdateAnnotationSource(data.source, data.name).subscribe({
          error: (result) => this.alertControlFacade.requestAddError(result),
        });
      });
    });
    markerTool.on('updateFreeformStyle', (data) => {
      this.zone.run(() => {
        this.slideAuthorFacade.requestUpdateFreeFormStyle(data.style, data.name).subscribe({
          error: (result) => this.alertControlFacade.requestAddError(result),
        });
      });
    });
    markerTool.on('selectAllMarkers', () => {
      this.annotations.forEach((annotation: FlatModels.ImageSharedModels.ElementModels) => {
        this.bulkEditAuthorFacade.requestToggleAnnotation(annotation.id).subscribe({
          error: (result) => this.alertControlFacade.requestAddError(result),
          next: () => {
            const image = this.slideAuthorFacade.snapshot_image();
            if (!image) return;
            this.typedImageAnnotationExpanded[image.id] = true;
            this.slideAuthorFacade.requestSetTypedImageAnnotationExpanded(this.typedImageAnnotationExpanded).subscribe({
              error: (result) => this.alertControlFacade.requestAddError(result),
            });
          }
        });
      });
    });
  }
  
  handleActivateFreeTool(freeform: FlatModels.AnnotationEntityModels.FreeForm) {
    this.selectAnnotation(freeform.id);
    if (!this.container) return;

    const element = this.container.getElementById(freeform.id);
    const shape = element || element._shape;
    if (shape.model && [FlatModels.AnnotationEntityModels.Shape.enum.free, FlatModels.AnnotationEntityModels.Shape.enum.interactiveArea].includes(shape.model.annotation.shape)) {
      // Show Slide Info message
      const dialogRef = this.dialogService.open(ModalControlComponent, {
        header: this.translate.instant('notification.free_form_draw_tool'),
        data: {
          inputLabel: 'global.remove',
          inputClass: FlatModels.ButtonDesignModels.ButtonDesignClass.PRIMARY + ' p-1',
          template: this.messageDrawTool
        },
        modal: false,
        position: 'bottom',
        width: '50%'
      });
      // Draw Tool
      element._shape.drawTool(this.viewer, this.overlay, this.imageIndex).then((data: FlatModels.ImageSharedModels.FreeformModel) => {
        this.zone.run(() => {
          if (!data.source || !data.freeFormStyle) return;
          this.slideAuthorFacade.requestUpdateFreeForm(data.annotation, data.source, data.freeFormStyle, 'history.free_form_update').subscribe({
            error: (result) => this.alertControlFacade.requestAddError(result),
          });
          this.slideAuthorFacade.requestSetAnnotationId(freeform.id, FlatModels.SlideAuthorModels.EditPanelDisplay.enum.image).subscribe({
            error: (result) => this.alertControlFacade.requestAddError(result),
            next: () => {
              this.zone.runOutsideAngular(() => {
                this.selectAnnotation(freeform.id);
              });
            }
          });
        });
        dialogRef.close();
      });
    }
  }
  
  // @ts-expect-error Parameter 'event' implicitly has an 'any' type.
  #handleSelectContainerElement(event) {
    if (!this.container || this.selftestMarker) return;
    
    this.zone.run(() => {
      if (event.event.event.ctrlKey || event.event.event.metaKey) {
        return this.bulkEditAuthorFacade.requestToggleAnnotation(event.item._model.id).subscribe({
          error: (result) => this.alertControlFacade.requestAddError(result),
          next: () => {
            if (event.item._model.annotation.images?.length) {
              this.typedImageAnnotationExpanded[event.item._model.annotation.images[0]] = true;
              this.slideAuthorFacade.requestSetTypedImageAnnotationExpanded(this.typedImageAnnotationExpanded).subscribe({
                error: (result) => this.alertControlFacade.requestAddError(result),
              });
            } else {
              this.typedSlideAnnotationsExpanded[event.item._model.annotation.slide] = true
              this.slideAuthorFacade.requestSetTypedSlideAnnotationsExpanded(this.typedSlideAnnotationsExpanded).subscribe({
                error: (result) => this.alertControlFacade.requestAddError(result),
              });
            }
          }
        });
      } else if (event.item && this.slideAuthorFacade.snapshot_currentSelectedSlideAnnotation()?.id !== event.item._model.id) {
        this.slideAuthorFacade.requestSetAnnotationId(event.item._model.id, FlatModels.SlideAuthorModels.EditPanelDisplay.enum.image).subscribe({
          error: (result) => this.alertControlFacade.requestAddError(result),
          next: () => {
            if (event.item._model.annotation.images?.length) {
              this.typedImageAnnotationExpanded[event.item._model.annotation.images[0]] = true;
              this.slideAuthorFacade.requestSetTypedImageAnnotationExpanded(this.typedImageAnnotationExpanded).subscribe({
                error: (result) => this.alertControlFacade.requestAddError(result),
              });
            } else {
              this.typedSlideAnnotationsExpanded[event.item._model.annotation.slide] = true
              this.slideAuthorFacade.requestSetTypedSlideAnnotationsExpanded(this.typedSlideAnnotationsExpanded).subscribe({
                error: (result) => this.alertControlFacade.requestAddError(result),
              });
            }
          }
        });
      }
    });
   }

  #handleUnSelectMarker() {
    this.viewer.setMouseNavEnabled(true);
    if (!this.container) return;
    if (this.selftestMarker) {
      const element = this.container.getSelftestMarkerElementById(this.selftestMarker.id);
      if (element) {
        this.container.removeElement(element);
      }
      this.selftestMarker = null;
    } else {
      this.zone.run(() => {
        this.slideAuthorFacade.requestSetAnnotationId(null, FlatModels.SlideAuthorModels.EditPanelDisplay.enum.list).subscribe({
          error: (result) => this.alertControlFacade.requestAddError(result),
        });
      });
      this.container.deselectAll();
    }
  }

  #handleMove(viewer: OpenSeadragon.Viewer | null,
              // @ts-expect-error Parameter 'event' implicitly has an 'any' type.
              event) {
    this.hasMoved = true;
    viewer.setMouseNavEnabled(false);
    if (event.item._model.type !== FlatModels.AnnotationEntityModels.AnnotationType.enum.Keymap) {
      /*
      const {width, height} = this.getDimensions(this.media);
      const sX = event.item._model.source.position.x;
      const sY = event.item._model.source.position.y;
      const currentImage = viewer.world.getItemAt(this.imageIndex ?? 0);
      */
      /*
      const point = currentImage?.imageToViewportCoordinates(
          sX * width,
          sY * height
      );
      */
      event.item.draw();
      // if (event.item._element.bounds._width !== 0 && event.item._element.bounds._height !== 0) {
      //   const bW = event.item._element.bounds._width;
      //   const bH = event.item._element.bounds._height;
      //   const fW = event.item._localPaper.view.bounds.width / bW;
      //   const fH = event.item._localPaper.view.bounds.height / bH;
      //   event.item._model.source.zoom = (fW > fH ? fH : fW) * (point.x > point.y ? point.x : point.y);
      // }
    } else {
      viewer.viewport.goHome();
    }
  }

  #handleEndMove(viewer: OpenSeadragon.Viewer | null) {
    if (this.overlay?.canvas.parentElement) {
      this.overlay.canvas.parentElement.style.cursor = 'default';
    }
    viewer.setMouseNavEnabled(true);
    // if (this.hasMoved) {}
    this.hasMoved = false;
  }

  #clickHandler(viewer: OpenSeadragon.Viewer | null,
                // @ts-expect-error Parameter 'event' implicitly has an 'any' type.
                event,
                markerTool: MarkerTool) {
    const point = new OpenSeadragon.Point(event.position.x, event.position.y);
    const viewportPoint = viewer.viewport.pointFromPixel(point);

    const currentImage = viewer.world.getItemAt(this.imageIndex || 0);
    const imagePoint = currentImage?.viewportToImageCoordinates(viewportPoint);
    if (!imagePoint) {
      return;
    }
    event.point = {};
    ({x: event.point.x, y: event.point.y} = imagePoint);
    event.event = event.originalEvent;

    // @ts-expect-error Property _handleMouseDown is protected and only accessible within class MarkerTool and its subclasses.
    markerTool._handleMouseDown.call(markerTool._tool, event);
  }

  #dragHandler(viewer: OpenSeadragon.Viewer | null,
               // @ts-expect-error Parameter 'event' implicitly has an 'any' type.
               event,
               markerTool: MarkerTool) {
    if (this.slideAuthorFacade.snapshot_currentSelectedSlideAnnotation() === null && !this.virtualPointerSelected) {
      return;
    }
    const delta = new OpenSeadragon.Point(event.delta.x, event.delta.y);
    const viewportPoint = viewer.viewport.deltaPointsFromPixels(delta);
    viewportPoint.y *= viewer.viewport._contentAspectRatio || 1;
    ({x: event.delta.x, y: event.delta.y} = viewportPoint);
    event.event = event.originalEvent;

    // @ts-expect-error Property _handleMouseDown is protected and only accessible within class MarkerTool and its subclasses.
    markerTool._handleMouseDrag.call(markerTool._tool, event);
  }

  // @ts-expect-error Parameter 'event' implicitly has an 'any' type.
  #releaseHandler(event,
                  markerTool: MarkerTool) {
    if (this.slideAuthorFacade.snapshot_currentSelectedSlideAnnotation() === null && !this.virtualPointerSelected) {
      return;
    }
    
    // @ts-expect-error Property _handleMouseDown is protected and only accessible within class MarkerTool and its subclasses.
    markerTool._handleMouseUp.call(markerTool._tool, event);
  }

  toggleVirtualPointer(showVirtualPointer?: boolean) {
    if (!this.virtualPointer) {
      return;
    }
    if (showVirtualPointer) {
      this.virtualPointer.state = 'visible';
      const currentImage = this.viewer.world.getItemAt(this.imageIndex || 0);
      if (currentImage) {
        const {x, y} = this.viewer.viewport.getCenter();
        const offset = currentImage.viewportToImageCoordinates(x, y);
        this.virtualPointer.model.offset.x = offset.x;
        this.virtualPointer.model.offset.y = offset.y;
        this.virtualPointer.arrange();
      }
    } else {
      this.virtualPointer.state = 'hidden';
    }
    if (!this.overlay) return;
    this.overlay.resize();
    this.overlay.resizeCanvas();
  }

  #toggleMarkers(showMarkers?: boolean): void {
    if (!this.container) {
      return;
    }
    if (!this.menuSharedFacade.snapshot_showSelftest()) {
      this.container.showMarkers(showMarkers);
    }
    this.container.arrange();
    this.container.update();
  }
  
  getCurrentPositionAndZoom() {
    const currentImage = this.viewer.world.getItemAt(this.imageIndex ?? 0);
    const point = currentImage?.viewportToImageCoordinates(this.viewer.viewport.getCenter(true));
    if (!this.media?.dimensions) {
      return null;
    }
    const {width, height} = this.media.dimensions;
    if (!width || !height) {
      return null
    }
    
    const viewportBounds = this.viewer.viewport.getBounds();
    const imageBounds = currentImage.viewportToImageRectangle(viewportBounds);
    const currentBounds = {
      x: imageBounds.x / width,
      y: imageBounds.y / height,
      width: imageBounds.width / width,
      height: imageBounds.height / height
    };
    const currentPosition = {
      x: point.x / width,
      y: point.y / height,
      height,
      width,
      zoom: this.viewer.viewport.getZoom(true),
    };
    const positionAndZoom: FlatModels.AnnotationEntityModels.PositionAndZoom = {
      currentBounds,
      currentPosition,
      isVM: this.media ? this.mediaControlService.isDeepzoom(this.media) : false
    }
    return positionAndZoom;
  }

  //<editor-fold desc="Group">

  onSelectedPoiChange(poi: FlatModels.PointOfInterestEntityModels.PointOfInterest,
                      media: FlatModels.MediaEntityModels.MediaImage | FlatModels.MediaEntityModels.MediaDeepzoom) {
    if (!poi || !media) {
      return;
    }
    const currentImage = this.viewer?.world.getItemAt(this.imageIndex || 0);
    if (!media.dimensions) {
      return;
    }
    const {width, height} = media.dimensions;
    if (!poi || !currentImage || !this.viewer || !width || !height) {
      return;
    }

    if (poi.bounds?.x === null || poi.bounds?.y === null || poi.bounds?.width === null || poi.bounds?.height === null) {
      return;
    }
    const bounds = currentImage.imageToViewportRectangle(
      new OpenSeadragon.Rect(
        poi.bounds.x * width,
        poi.bounds.y * height,
        poi.bounds.width * width,
        poi.bounds.height * height
      )
    )
    
    this.viewer.viewport.fitBounds(bounds, false);
  }

  //</editor-fold>

  //<editor-fold desc="Annotation">

  selectAnnotation(id: string) {
    if (this.container) {
      const element = this.container.getElementById(id);
      if (element) {
        this.container.select(element, false);
      }
    }
  }

  goToAnnotation(annotation: FlatModels.AnnotationEntityModels.AnnotationEntityType) {
    const currentImage = this.viewer.world.getItemAt(this.imageIndex ?? 0);
    const obj = this.container?.elements.find(e => e.id === annotation.id);

    if (!annotation || !currentImage || !obj?.element?.handleBounds) return;

    const padding = { x: 0.4, y: 0.2 };
    const bounds = obj.element.handleBounds;
    const viewportBounds = currentImage.imageToViewportRectangle(
      new OpenSeadragon.Rect(
        bounds.x - (bounds.width * padding.x),
        bounds.y - (bounds.height * padding.y),
        bounds.width + (bounds.width * padding.x * 2),
        bounds.height + (bounds.height * padding.y * 2),
      )
    );

    this.viewer?.viewport.fitBoundsWithConstraints(viewportBounds, false);
  }

  goToPoi(poi: FlatModels.PointOfInterestEntityModels.PointOfInterest) {
    const currentImage = this.viewer?.world.getItemAt(this.imageIndex ?? 0);
    if (!this.media?.dimensions) {
      return
    }
    const {width, height} = this.media.dimensions;
    if (!poi || !currentImage || !this.viewer || !width || !height) {
      return;
    }
    if (poi.bounds?.x === null || poi.bounds?.y === null || poi.bounds?.width === null || poi.bounds?.height === null) {
      return;
    }
    const bounds = currentImage.imageToViewportRectangle(
      new OpenSeadragon.Rect(
        poi.bounds.x * width,
        poi.bounds.y * height,
        poi.bounds.width * width,
        poi.bounds.height * height
      )
    )
    
    this.viewer?.viewport.fitBounds(bounds, false);
  }

  onSelectedAnnotationChange(annotation: FlatModels.AnnotationEntityModels.AnnotationEntityType | null): void {
    if (this.container) {
      this.container.deselectAll();
    }
    if (annotation) {
      this.selectAnnotation(annotation.id);
    }
  }

  //</editor-fold>

  //<editor-fold desc="Links">

  goToLink(id: string) {
    const url: string = this.router.serializeUrl(this.router.createUrlTree(['folien', id]));
    window.open(url, '_blank');
  }

  goToExternalLink(url: string): void {
    window.open(url, '_blank');
  }

  //</editor-fold>

  //<editor-fold desc="Host Listener">
  
  @HostListener('contextmenu', ['$event'])
  onRightClick(event: Event) {
    event.preventDefault();
  }
  
  @HostListener('window:keyup', ['$event'])
  handleKeyUp(event: KeyboardEvent) {
    if (event.key === 'Alt' && this.viewer) {
      event.preventDefault();
      this.viewer.gestureSettingsMouse.scrollToZoom = true;
    }
  }
  
  @HostListener('window:keydown', ['$event'])
  handleKeyDown(event: KeyboardEvent) {
    if (event.key === 'Alt' && this.viewer) {
      event.preventDefault();
      this.viewer.gestureSettingsMouse.scrollToZoom = false;
    }

    if (this.mouseOverCanvas) {
      switch (event.key) {
        case 'PageUp':
        case 'j':
          this.slideAuthorFacade.requestSetImageIndex(this.layerIndex, this.imageIndex - 1).subscribe({
            error: (result) => this.alertControlFacade.requestAddError(result),
          });
          break;
        case 'PageDown':
        case 'k':
          this.slideAuthorFacade.requestSetImageIndex(this.layerIndex, this.imageIndex + 1).subscribe({
            error: (result) => this.alertControlFacade.requestAddError(result),
          });
          break;
        /* Remove Virtual Pointer from Author
        case 'z':
          this.menuSharedFacade.requestToggleVirtualPointer().subscribe({
            error: (result) => this.alertControlFacade.requestAddError(result),
          });
          break;
        */
      }
      
      if (event.ctrlKey) {
        let page: FlatModels.PageEntityModels.Slide | FlatModels.PageEntityModels.Training | null;
        let image: FlatModels.ImageEntityModels.Image | null;
        let currentPositionAndZoom;
        switch (event.key) {
          case 'c':
            this.bulkEditAuthorFacade.requestCopyAnnotationsFromSelectSet().subscribe({
              error: (result) => this.alertControlFacade.requestAddError(result),
            });
            break;
          case 'v':
            page = this.slideAuthorFacade.snapshot_page();
            image = this.slideAuthorFacade.snapshot_image();
            if (!page) {
              return
            }
            currentPositionAndZoom = this.getCurrentPositionAndZoom();
            this.bulkEditAuthorFacade.requestPasteAnnotations(page, image, currentPositionAndZoom).subscribe({
              error: (result) => this.alertControlFacade.requestAddError(result),
              next: () => {
                if (!image) return;
                this.typedImageAnnotationExpanded[image.id] = true;
                this.slideAuthorFacade.requestSetTypedImageAnnotationExpanded(this.typedImageAnnotationExpanded).subscribe({
                  error: (result) => this.alertControlFacade.requestAddError(result),
                });
              }
            });
            break;
        }
      }

      if (event.altKey) {
        switch (event.key) {
          case 'ArrowUp':
            this.slideAuthorFacade.requestSetImageIndex(this.layerIndex, this.imageIndex - 1).subscribe({
              error: (result) => this.alertControlFacade.requestAddError(result),
            });
            break;
          case 'ArrowDown':
            this.slideAuthorFacade.requestSetImageIndex(this.layerIndex, this.imageIndex + 1).subscribe({
              error: (result) => this.alertControlFacade.requestAddError(result),
            });
            break;
        }
      }
    }
  }

  //</editor-fold>

  handleWheel(event: WheelEvent) {
    if (event.altKey) {
      event.stopImmediatePropagation();
      const delta = Math.sign(event.deltaY);
      this.slideAuthorFacade.requestSetImageIndex(this.layerIndex, this.imageIndex + delta).subscribe({
        error: (result) => this.alertControlFacade.requestAddError(result),
      });
    }
  }

  ngOnDestroy(): void {
    window.removeEventListener('wheel', this.handleWheel);
  }
}
